import { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Tag, Button, Modal, Form, Input, Select, DatePicker,
  InputNumber, Radio, Tabs, Space, Popconfirm, message,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { vppCustomers } from '../../../services/api';

// 当前用户角色：仅 admin 有写操作按钮（与后端 requireAdmin 一致）
const user = JSON.parse(localStorage.getItem('qhy_user') || '{}');
const isAdmin = user.role === 'admin';

const CUSTOMER_TYPES = ['代理用户', '资源方', '居间商'];
const CUSTOMER_STATUSES = ['潜在', '意向', '签约', '退出'];
const CONTRACT_TYPES = ['代理', '聚合', '控制授权', '售电'];
const CONTRACT_STATUSES = ['草稿', '执行中', '已到期', '已终止'];

const CUSTOMER_STATUS_COLORS = { 潜在: 'default', 意向: 'gold', 签约: 'green', 退出: 'red' };
const CONTRACT_STATUS_COLORS = { 草稿: 'default', 执行中: 'green', 已到期: 'orange', 已终止: 'red' };

// 分成条款摘要展示
function shareTermsText(terms) {
  if (!terms) return '—';
  const t = typeof terms === 'string' ? (() => { try { return JSON.parse(terms); } catch { return null; } })() : terms;
  if (!t) return '—';
  if (t.mode === 'fixed') return `固定比例 ${t.ratio}%`;
  if (t.mode === 'tiered') return `阶梯比例 ${(t.tiers || []).length} 档`;
  return '—';
}

// 用电信息表单值 -> 后端 JSON 对象
function buildElectricityInfo(values) {
  const info = {};
  if (values.voltage_level) info.voltage_level = values.voltage_level;
  if (values.transformer_capacity != null) info.transformer_capacity_kva = values.transformer_capacity;
  if (values.annual_consumption != null) info.annual_consumption_kwh = values.annual_consumption;
  return Object.keys(info).length > 0 ? info : null;
}

export default function VppCustomers() {
  const [activeTab, setActiveTab] = useState('customers');

  // 客户
  const [customers, setCustomers] = useState([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [customerModal, setCustomerModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [customerForm] = Form.useForm();

  // 合同
  const [contracts, setContracts] = useState([]);
  const [contractLoading, setContractLoading] = useState(false);
  const [expiringDays, setExpiringDays] = useState(30);
  const [contractModal, setContractModal] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [contractForm] = Form.useForm();
  const shareMode = Form.useWatch('share_mode', contractForm);

  const loadCustomers = useCallback(async () => {
    setCustomerLoading(true);
    try {
      const res = await vppCustomers.list();
      setCustomers(res.data || []);
    } catch { /* 拦截器已提示 */ } finally {
      setCustomerLoading(false);
    }
  }, []);

  const loadContracts = useCallback(async () => {
    setContractLoading(true);
    try {
      const res = await vppCustomers.contracts();
      setContracts(res.data || []);
      if (res.expiring_days) setExpiringDays(res.expiring_days);
    } catch { /* 拦截器已提示 */ } finally {
      setContractLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCustomers();
    loadContracts();
  }, [loadCustomers, loadContracts]);

  // ============ 客户操作 ============

  const openCustomerModal = (record) => {
    setEditingCustomer(record || null);
    if (record) {
      const info = record.electricity_info || {};
      customerForm.setFieldsValue({
        ...record,
        voltage_level: info.voltage_level,
        transformer_capacity: info.transformer_capacity_kva,
        annual_consumption: info.annual_consumption_kwh,
      });
    } else {
      customerForm.resetFields();
    }
    setCustomerModal(true);
  };

  const saveCustomer = async () => {
    const values = await customerForm.validateFields();
    const payload = {
      name: values.name,
      type: values.type,
      status: values.status,
      contact_person: values.contact_person,
      contact_phone: values.contact_phone,
      email: values.email,
      address: values.address,
      account_no: values.account_no,
      electricity_info: buildElectricityInfo(values),
    };
    try {
      if (editingCustomer) {
        await vppCustomers.update(editingCustomer.id, payload);
        message.success('客户已更新');
      } else {
        await vppCustomers.create(payload);
        message.success('客户已创建');
      }
      setCustomerModal(false);
      loadCustomers();
    } catch { /* 拦截器已提示 */ }
  };

  const removeCustomer = async (record) => {
    try {
      await vppCustomers.delete(record.id);
      message.success('客户已删除');
      loadCustomers();
    } catch { /* 拦截器已提示 */ }
  };

  // ============ 合同操作 ============

  const openContractModal = (record) => {
    setEditingContract(record || null);
    if (record) {
      const t = typeof record.share_terms === 'string'
        ? (() => { try { return JSON.parse(record.share_terms); } catch { return null; } })()
        : record.share_terms;
      contractForm.setFieldsValue({
        ...record,
        dateRange: [dayjs(record.start_date), dayjs(record.end_date)],
        share_mode: t?.mode || 'fixed',
        share_ratio: t?.mode === 'fixed' ? t.ratio : undefined,
        share_tiers: t?.mode === 'tiered' ? JSON.stringify(t.tiers, null, 2) : undefined,
      });
    } else {
      contractForm.resetFields();
    }
    setContractModal(true);
  };

  const saveContract = async () => {
    const values = await contractForm.validateFields();
    let share_terms = null;
    if (values.share_mode === 'fixed') {
      share_terms = { mode: 'fixed', ratio: values.share_ratio };
    } else {
      let tiers;
      try {
        tiers = JSON.parse(values.share_tiers);
      } catch {
        message.error('阶梯分成 JSON 格式错误');
        return;
      }
      share_terms = { mode: 'tiered', tiers };
    }
    const payload = {
      contract_no: values.contract_no,
      customer_id: values.customer_id,
      type: values.type,
      start_date: values.dateRange[0].format('YYYY-MM-DD'),
      end_date: values.dateRange[1].format('YYYY-MM-DD'),
      share_terms,
      penalty_terms: values.penalty_terms,
      battery_compensation_terms: values.battery_compensation_terms,
      status: values.status,
      remark: values.remark,
    };
    try {
      if (editingContract) {
        await vppCustomers.updateContract(editingContract.id, payload);
        message.success('合同已更新');
      } else {
        await vppCustomers.createContract(payload);
        message.success('合同已创建');
      }
      setContractModal(false);
      loadContracts();
    } catch { /* 拦截器已提示 */ }
  };

  const removeContract = async (record) => {
    try {
      await vppCustomers.deleteContract(record.id);
      message.success('合同已删除');
      loadContracts();
    } catch { /* 拦截器已提示 */ }
  };

  // ============ 表格列 ============

  const customerColumns = [
    { title: '客户名称', dataIndex: 'name' },
    { title: '类型', dataIndex: 'type', render: (v) => <Tag>{v}</Tag> },
    { title: '联系人', dataIndex: 'contact_person', render: (v) => v || '—' },
    { title: '联系电话', dataIndex: 'contact_phone', render: (v) => v || '—' },
    { title: '户号', dataIndex: 'account_no', render: (v) => v || '—' },
    {
      title: '状态', dataIndex: 'status',
      render: (v) => <Tag color={CUSTOMER_STATUS_COLORS[v] || 'default'}>{v || '潜在'}</Tag>,
    },
    ...(isAdmin ? [{
      title: '操作', width: 150,
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openCustomerModal(record)}>编辑</Button>
          <Popconfirm title="确认删除该客户？" onConfirm={() => removeCustomer(record)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    }] : []),
  ];

  const contractColumns = [
    { title: '合同编号', dataIndex: 'contract_no' },
    { title: '客户', dataIndex: 'customer_name', render: (v) => v || '—' },
    { title: '类型', dataIndex: 'type', render: (v) => <Tag>{v}</Tag> },
    { title: '起止日期', render: (_, r) => `${r.start_date} ~ ${r.end_date}` },
    { title: '分成条款', dataIndex: 'share_terms', render: shareTermsText },
    {
      title: '状态', dataIndex: 'status',
      render: (v) => <Tag color={CONTRACT_STATUS_COLORS[v] || 'default'}>{v}</Tag>,
    },
    {
      title: '到期提醒', dataIndex: 'expiring',
      render: (_, r) => {
        if (r.expiring) return <Tag color="red">临期（剩{r.days_to_expire}天）</Tag>;
        if (r.days_to_expire != null && r.days_to_expire < 0) return <Tag color="orange">已过期</Tag>;
        return <Tag color="default">正常</Tag>;
      },
    },
    ...(isAdmin ? [{
      title: '操作', width: 150,
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openContractModal(record)}>编辑</Button>
          <Popconfirm title="确认删除该合同？" onConfirm={() => removeContract(record)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    }] : []),
  ];

  return (
    <Card size="small" title="客户与合同管理">
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'customers',
            label: '客户管理',
            children: (
              <>
                {isAdmin && (
                  <Button type="primary" icon={<PlusOutlined />} style={{ marginBottom: 16 }} onClick={() => openCustomerModal(null)}>
                    新建客户
                  </Button>
                )}
                <Table
                  columns={customerColumns}
                  dataSource={customers.map((c) => ({ ...c, key: c.id }))}
                  loading={customerLoading}
                  size="small"
                  locale={{ emptyText: '暂无客户，点击「新建客户」建立客户档案' }}
                />
              </>
            ),
          },
          {
            key: 'contracts',
            label: '合同管理',
            children: (
              <>
                <Space style={{ marginBottom: 16 }}>
                  {isAdmin && (
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => openContractModal(null)}>
                      新建合同
                    </Button>
                  )}
                  <span style={{ color: '#999' }}>执行中合同到期前 {expiringDays} 天标记「临期」</span>
                </Space>
                <Table
                  columns={contractColumns}
                  dataSource={contracts.map((c) => ({ ...c, key: c.id }))}
                  loading={contractLoading}
                  size="small"
                  locale={{ emptyText: '暂无合同，点击「新建合同」录入代理/聚合合同' }}
                />
              </>
            ),
          },
        ]}
      />

      {/* 客户编辑弹窗 */}
      <Modal
        title={editingCustomer ? '编辑客户' : '新建客户'}
        open={customerModal}
        onOk={saveCustomer}
        onCancel={() => setCustomerModal(false)}
        width={640}
        destroyOnClose
      >
        <Form form={customerForm} layout="vertical" initialValues={{ type: '资源方', status: '潜在' }}>
          <Form.Item name="name" label="客户名称" rules={[{ required: true, message: '请输入客户名称' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="type" label="客户类型" rules={[{ required: true }]}>
            <Radio.Group options={CUSTOMER_TYPES.map((t) => ({ label: t, value: t }))} optionType="button" />
          </Form.Item>
          <Form.Item name="status" label="客户状态" rules={[{ required: true }]}>
            <Select options={CUSTOMER_STATUSES.map((s) => ({ label: s, value: s }))} />
          </Form.Item>
          <Form.Item name="contact_person" label="联系人"><Input /></Form.Item>
          <Form.Item name="contact_phone" label="联系电话"><Input /></Form.Item>
          <Form.Item name="email" label="邮箱"><Input /></Form.Item>
          <Form.Item name="address" label="地址"><Input /></Form.Item>
          <Form.Item name="account_no" label="户号（供电客户编号）"><Input /></Form.Item>
          <Form.Item name="voltage_level" label="电压等级（如 10kV）"><Input placeholder="选填" /></Form.Item>
          <Form.Item name="transformer_capacity" label="变压器容量(kVA)"><InputNumber style={{ width: '100%' }} min={0} placeholder="选填" /></Form.Item>
          <Form.Item name="annual_consumption" label="年用电量(kWh)"><InputNumber style={{ width: '100%' }} min={0} placeholder="选填" /></Form.Item>
        </Form>
      </Modal>

      {/* 合同编辑弹窗 */}
      <Modal
        title={editingContract ? '编辑合同' : '新建合同'}
        open={contractModal}
        onOk={saveContract}
        onCancel={() => setContractModal(false)}
        width={720}
        destroyOnClose
      >
        <Form form={contractForm} layout="vertical" initialValues={{ type: '代理', status: '草稿', share_mode: 'fixed' }}>
          <Form.Item name="contract_no" label="合同编号" rules={[{ required: true, message: '请输入合同编号' }]}>
            <Input placeholder="如 QHY-HT-2026-001" />
          </Form.Item>
          <Form.Item name="customer_id" label="关联客户" rules={[{ required: true, message: '请选择客户' }]}>
            <Select
              showSearch
              optionFilterProp="label"
              options={customers.map((c) => ({ label: `${c.name}（${c.type}）`, value: c.id }))}
            />
          </Form.Item>
          <Form.Item name="type" label="合同类型" rules={[{ required: true }]}>
            <Select options={CONTRACT_TYPES.map((t) => ({ label: t, value: t }))} />
          </Form.Item>
          <Form.Item name="dateRange" label="合同起止日期" rules={[{ required: true, message: '请选择起止日期' }]}>
            <DatePicker.RangePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="share_mode" label="分成方式" rules={[{ required: true }]}>
            <Radio.Group
              options={[{ label: '固定比例', value: 'fixed' }, { label: '阶梯比例', value: 'tiered' }]}
              optionType="button"
            />
          </Form.Item>
          {shareMode !== 'tiered' ? (
            <Form.Item name="share_ratio" label="平台分成比例(%)" rules={[{ required: true, message: '请输入分成比例' }]}>
              <InputNumber style={{ width: '100%' }} min={0} max={100} precision={2} />
            </Form.Item>
          ) : (
            <Form.Item
              name="share_tiers"
              label="阶梯分成规则（JSON）"
              rules={[{ required: true, message: '请输入阶梯规则' }]}
              extra='按收益区间分档，如 [{"min":0,"max":10000,"ratio":30},{"min":10000,"max":null,"ratio":25}]'
            >
              <Input.TextArea rows={3} placeholder='[{"min":0,"max":10000,"ratio":30}]' />
            </Form.Item>
          )}
          <Form.Item name="penalty_terms" label="考核承担条款">
            <Input.TextArea rows={2} placeholder="如：考核费用由资源方承担70%，平台承担30%" />
          </Form.Item>
          <Form.Item name="battery_compensation_terms" label="电池损耗补偿条款">
            <Input.TextArea rows={2} placeholder="如：按实际放电量 0.05 元/kWh 计提电池损耗补偿" />
          </Form.Item>
          <Form.Item name="status" label="合同状态" rules={[{ required: true }]}>
            <Select options={CONTRACT_STATUSES.map((s) => ({ label: s, value: s }))} />
          </Form.Item>
          <Form.Item name="remark" label="备注"><Input /></Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
